
import { Eye, SearchCode, BellRing, ShieldAlert } from "lucide-react";
import { SecurityCard } from "./SecurityCard";

export const ApproachSection = () => {
  const securityServices = [
    {
      icon: Eye,
      title: "See Every Threat Coming Your Way",
      description: "You'll sleep better knowing that sophisticated monitoring is working around the clock:",
      features: [
        {
          title: "Complete Visibility",
          description: "You'll have a clear view of everything happening across your digital environment, eliminating dangerous blind spots."
        },
        {
          title: "Early Warning System",
          description: "You'll be alerted to suspicious activities before they become full-blown attacks, not after the damage is done."
        },
        {
          title: "Contextual Understanding",
          description: "Your security system will understand what's normal for your business, reducing false alarms that cause unnecessary stress."
        }
      ],
      testimonial: "I used to worry constantly about what we might be missing. Now I know if something suspicious happens, we'll catch it immediately.",
      testimonialAuthor: "Operations Director, Healthcare Provider"
    },
    {
      icon: SearchCode,
      title: "Close Your Security Gaps",
      description: "You'll systematically strengthen your defenses before attackers can exploit weaknesses:",
      features: [
        {
          title: "Continuous Vulnerability Discovery",
          description: "Your systems will be regularly scanned to uncover weaknesses before attackers can find and exploit them."
        },
        {
          title: "Prioritized Risk Reduction",
          description: "You'll know exactly which vulnerabilities to address first based on your specific business risks, not generic checklists."
        },
        {
          title: "Proactive Protection",
          description: "Your systems will stay updated against the latest threats without disrupting your business operations."
        }
      ],
      testimonial: "We went from constantly playing catch-up with patches to having a systematic approach that keeps us ahead of threats.",
      testimonialAuthor: "IT Manager, Financial Services"
    },
    {
      icon: BellRing,
      title: "Respond Quickly, Recover Stronger",
      description: "If an incident occurs, you'll have immediate expert response:",
      features: [
        {
          title: "Ready-to-Execute Response Plan",
          description: "You'll have a customized plan that activates immediately when an incident occurs, not scrambling to figure out what to do."
        },
        {
          title: "Expert Response Team",
          description: "You'll have immediate access to security experts who can contain and resolve incidents quickly."
        },
        {
          title: "Business Continuity Focus",
          description: "Your response prioritizes keeping your business running while addressing the threat, minimizing operational impact."
        }
      ],
      testimonial: "When we had a ransomware attempt, the team contained it within 30 minutes. Without them, it would have been catastrophic.",
      testimonialAuthor: "CEO, Legal Services Firm"
    },
    {
      icon: ShieldAlert,
      title: "Security That Grows With You",
      description: "Your protection evolves alongside your business needs:",
      features: [
        {
          title: "Future-Proof Security Architecture",
          description: "Your security framework will adapt to new technologies and business changes without requiring complete redesigns."
        },
        {
          title: "Secure Digital Transformation",
          description: "You'll be able to adopt new technologies confidently knowing security is built in from the start, not bolted on later."
        },
        {
          title: "Scalable Protection",
          description: "Your security will scale seamlessly as your business grows, maintaining protection without increasing complexity."
        }
      ],
      testimonial: "As we expanded to three new locations and doubled our remote workforce, our security scaled perfectly without missing a beat.",
      testimonialAuthor: "CTO, Manufacturing Company"
    }
  ];

  return (
    <section className="py-24 bg-gray-50" id="approach">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center mb-20">
          <span className="inline-block px-4 py-1.5 bg-edgefi-purple/10 text-edgefi-purple rounded-full text-sm font-medium mb-6">Your Security Transformation</span>
          <h2 className="text-3xl sm:text-4xl font-bold mb-8 text-gray-900">
            How Your Security Experience Will Transform
          </h2>
          <p className="text-xl text-gray-600">
            Experience a fundamental shift from reactive worry to proactive confidence in your security posture.
          </p>
        </div>
        
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {securityServices.map((service, index) => (
              <SecurityCard 
                key={index}
                icon={service.icon}
                title={service.title}
                description={service.description}
                features={service.features}
                testimonial={service.testimonial}
                testimonialAuthor={service.testimonialAuthor}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
